'use client'

import { useState, useEffect, useRef } from 'react'
import { useEventListener } from 'usehooks-ts'

import { reloadPage } from '@marketplace-web/shared/browser-util'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { UiState, useLatestCallback } from '@marketplace-web/shared/ui-state-util'
import {
  addSelectedFilter,
  removeSelectedFilter,
  DynamicFilterModel,
  SelectedDynamicFilterModel,
  DisplayType,
  OptionType,
  SelectionType,
} from '@marketplace-web/catalog/catalog-data'
import {
  DynamicFilterReducerAction,
  useCatalogContext,
  clickEvent,
} from '@marketplace-web/catalog/catalog-page-data'

import useFacetChangeHandler from '../../../hooks/useFacetChangeHandler'
import GridFilterDropdown from '../common/DynamicFilters/GridFilter/GridFilterDropdown'
import ListFilterDropdown from '../common/DynamicFilters/ListFilter/ListFilterDropdown'
import SearchFilterDropdown from '../common/DynamicFilters/SearchFilter/SearchFilterDropdown'
import useFetchDynamicFilters from '../../../hooks/useFetchDynamicFilters'
import useFetchCatalogBrand from '../../../hooks/useFetchCatalogBrand'
import useFetchDynamicFiltersFacets from '../../../hooks/useFetchDynamicFiltersFacets'
import CatalogFilter from './CatalogFilter'
import PriceFilter from './PriceFilter'
import SortFilter from './SortFilter'

type Props = {
  learnMoreUrl?: string | null
}

const Filters = ({ learnMoreUrl }: Props) => {
  const { track } = useTracking()
  const {
    state: {
      dynamicFilters: {
        dynamicFilters,
        selectedDynamicFilters,
        filterFacets: { filterFacetsCount },
        ui: { uiState },
      },
    },
    dispatch,
  } = useCatalogContext()
  const fetchDynamicFilters = useFetchDynamicFilters()
  const fetchCatalogBrand = useFetchCatalogBrand()
  const fetchDynamicFiltersFacets = useFetchDynamicFiltersFacets()
  const [totalItemsCount, setTotalItemsCount] = useState<number | null>(null)
  const [isLazyLoading, setIsLazyLoading] = useState(false)
  const deferredLazyFilterRef = useRef<string | null>(null)
  const [openedLazyFilterType, setOpenedLazyFilterType] = useState<string | null>(null)

  useEventListener('popstate', reloadPage)

  const handleItemFacetChange = useFacetChangeHandler({
    dynamicFiltersList: dynamicFilters,
    setTotalItemsCount,
    fetchDynamicFiltersFacets,
  })

  const recalculateTotalItemCount = useLatestCallback((filterType: string) => {
    handleItemFacetChange(selectedDynamicFilters, filterType)
  })

  const fetchLazyFacetedOptions = useLatestCallback(async (type: string) => {
    await fetchDynamicFiltersFacets({
      selectedDynamicFilters,
      filterCode: type,
      isLazy: true,
    })

    setIsLazyLoading(false)

    // defer the calculation to the effect after re-render
    setOpenedLazyFilterType(type)
  })

  useEffect(() => {
    if (!openedLazyFilterType) return

    handleItemFacetChange(selectedDynamicFilters, openedLazyFilterType)
    setOpenedLazyFilterType(null)
  }, [openedLazyFilterType, handleItemFacetChange, selectedDynamicFilters])

  useEffect(() => {
    if (uiState !== UiState.Success || !deferredLazyFilterRef.current) return

    setIsLazyLoading(true)

    fetchLazyFacetedOptions(deferredLazyFilterRef.current)
    deferredLazyFilterRef.current = null
  }, [uiState, fetchLazyFacetedOptions])

  const handleFilterOpen = (type: string, isLazy?: boolean) => {
    // reset filter facet count when entering a filter
    dispatch({
      type: DynamicFilterReducerAction.SET_DYNAMIC_FILTER_FACETS_COUNTS,
      payload: { filterFacetsCount: null },
    })

    // fetch options if filter is lazy and there is no ongoing filter fetch operation
    if (isLazy) {
      setIsLazyLoading(true)
      if (uiState === UiState.Success) {
        fetchLazyFacetedOptions(type)
      } else {
        // we are delaying fetching facets until ongoing filters are done
        deferredLazyFilterRef.current = type
      }
    } else if (type) {
      handleItemFacetChange(selectedDynamicFilters, type)
    }
  }

  const handleFilterClose = () => {
    setIsLazyLoading(false)
  }

  const handleFilterUpdate = useLatestCallback(
    (updatedSelectedFilters: Array<SelectedDynamicFilterModel>, type: string) => {
      dispatch({
        type: DynamicFilterReducerAction.SET_SELECTED_DYNAMIC_FILTERS,
        payload: { selectedDynamicFilters: updatedSelectedFilters },
      })

      handleItemFacetChange(updatedSelectedFilters, type)
    },
  )

  const handleAddSelectedFilter = useLatestCallback(
    (newFilter: { type: string; ids: Array<number> }) => {
      const updatedSelectedFilters = addSelectedFilter(newFilter, selectedDynamicFilters)

      handleFilterUpdate(updatedSelectedFilters, newFilter.type)
    },
  )

  const handleRemoveSelectedFilter = useLatestCallback(
    (removeFilter: { type: string; id: number }) => {
      const updatedSelectedFilters = removeSelectedFilter(removeFilter, selectedDynamicFilters)

      handleFilterUpdate(updatedSelectedFilters, removeFilter.type)
    },
  )

  const handleDynamicFilterToggle = useLatestCallback(
    (id: number, type: string, selectionType: SelectionType, selectedIds: Array<number>) => {
      if (selectedIds.includes(id)) {
        if (selectionType === SelectionType.Multi) {
          handleRemoveSelectedFilter({ type, id })
        }
      } else {
        const newIds = selectionType === SelectionType.Single ? [id] : [...selectedIds, id]
        handleAddSelectedFilter({ type, ids: newIds })
      }
    },
  )

  const handleSubmit = () => {
    fetchDynamicFilters({ selectedDynamicFilters })
    fetchCatalogBrand({ selectedDynamicFilters })

    track(
      clickEvent({
        target: 'apply_filters',
        screen: 'catalog',
      }),
    )
  }

  const getRootOptionType = (dynamicFilter: DynamicFilterModel) => {
    return dynamicFilter.options.find(option => !!option.type)?.type || OptionType.Default
  }

  const getSelectedFilter = (type: string) => {
    return selectedDynamicFilters.find(filter => filter.type === type)
  }

  const renderDynamicFilter = (filter: DynamicFilterModel) => {
    const selectedDynamicFilter = getSelectedFilter(filter.type)
    const key = `${filter.type}-${filter.id}`
    const noOptionSelected = !selectedDynamicFilter || selectedDynamicFilter?.ids.length === 0
    let totalFacetsCount: number | null | undefined

    if (filter.isAsyncFacet) {
      totalFacetsCount = noOptionSelected ? null : filterFacetsCount
    } else {
      totalFacetsCount = noOptionSelected || totalItemsCount == null ? null : totalItemsCount
    }

    switch (filter.displayType) {
      case DisplayType.List: {
        return (
          <ListFilterDropdown
            key={key}
            type={filter.type}
            title={filter.title}
            selectionType={filter.selectionType}
            isSelectionHighlighted={filter.isSelectionHighlighted}
            isHidden={filter.isHidden}
            experiments={filter.experiments}
            links={filter.links}
            totalItemsCount={totalFacetsCount}
            totalCountMax={filter.totalCountMax}
            isAsyncFacet={filter.isAsyncFacet}
            isLazy={filter.isLazy}
            isLoading={isLazyLoading}
            translations={filter.translations}
            rootOptionType={getRootOptionType(filter)}
            options={filter.options}
            fromScreen="catalog"
            selectedIds={selectedDynamicFilter?.ids || []}
            onSubmit={handleSubmit}
            onFilterOpen={() => handleFilterOpen(filter.type, filter.isLazy)}
            onFilterClose={handleFilterClose}
            onListOptionSelect={id =>
              handleDynamicFilterToggle(
                id,
                filter.type,
                filter.selectionType,
                selectedDynamicFilter?.ids || [],
              )
            }
          />
        )
      }
      case DisplayType.ListSearch: {
        return (
          <SearchFilterDropdown
            key={key}
            type={filter.type}
            title={filter.title}
            selectionType={filter.selectionType}
            isSelectionHighlighted={filter.isSelectionHighlighted}
            isHidden={filter.isHidden}
            experiments={filter.experiments}
            links={filter.links}
            totalItemsCount={totalFacetsCount}
            totalCountMax={filter.totalCountMax}
            isAsyncFacet={filter.isAsyncFacet}
            isLazy={filter.isLazy}
            isLoading={isLazyLoading}
            rootOptionType={getRootOptionType(filter)}
            options={filter.options}
            fromScreen="catalog"
            translations={filter.translations}
            searchTranslations={filter.searchTranslations}
            selectedIds={selectedDynamicFilter?.ids || []}
            onSubmit={handleSubmit}
            onFilterOpen={() => handleFilterOpen(filter.type, filter.isLazy)}
            onFilterClose={handleFilterClose}
            onItemCountRecalculate={recalculateTotalItemCount}
            onListOptionSelect={id =>
              handleDynamicFilterToggle(
                id,
                filter.type,
                filter.selectionType,
                selectedDynamicFilter?.ids || [],
              )
            }
          />
        )
      }
      case DisplayType.Grid: {
        return (
          <GridFilterDropdown
            key={key}
            type={filter.type}
            title={filter.title}
            fromScreen="catalog"
            isSelectionHighlighted={filter.isSelectionHighlighted || false}
            isHidden={filter.isHidden}
            experiments={filter.experiments}
            rootOptionType={getRootOptionType(filter)}
            options={filter.options}
            selectedIds={selectedDynamicFilter?.ids || []}
            onListOptionSelect={id =>
              handleDynamicFilterToggle(
                id,
                filter.type,
                filter.selectionType,
                selectedDynamicFilter?.ids || [],
              )
            }
          />
        )
      }
      case DisplayType.HybridPrice: {
        return <PriceFilter key={key} minimumValidation={filter.minimumValidation} />
      }
      default:
        return null
    }
  }

  const renderDynamicFilters = () => {
    return dynamicFilters.map(filter => renderDynamicFilter(filter))
  }

  return (
    <div className="u-flexbox u-flex-wrap">
      <CatalogFilter />
      {renderDynamicFilters()}
      <SortFilter learnMoreUrl={learnMoreUrl} />
    </div>
  )
}

export default Filters
